import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Lenis from "lenis";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const SUPPORT_EMAIL = "support@blackdiamondfacility.ca";

const DiamondMark = ({ size = 120 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
    <defs>
      <linearGradient id="cs-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#F2F2F7" />
        <stop offset="0.5" stopColor="#8E8E93" />
        <stop offset="1" stopColor="#1C1C1E" />
      </linearGradient>
    </defs>
    <path
      d="M50 6 L92 40 L50 94 L8 40 Z"
      fill="url(#cs-g)"
      stroke="#F2F2F7"
      strokeWidth="1.5"
    />
    <path d="M50 6 L68 40 L50 94 L32 40 Z" fill="#050505" opacity="0.35" />
    <path d="M8 40 L92 40" stroke="#F2F2F7" strokeWidth="1" opacity="0.6" />
    <path
      d="M32 40 L50 6 L68 40"
      stroke="#F2F2F7"
      strokeWidth="0.8"
      opacity="0.5"
      fill="none"
    />
  </svg>
);

const Grain = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-0 z-50 opacity-[0.055] mix-blend-screen"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
    }}
  />
);

const MaskedLine = ({ children, delay, className = "", testId }) => (
  <div className="overflow-hidden py-[0.4vw]">
    <motion.span
      data-testid={testId}
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </div>
);

export default function ComingSoon() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 50,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 50,
    damping: 16,
  });

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <div
      data-testid="coming-soon-page"
      onMouseMove={onMove}
      className="flex min-h-screen flex-col bg-[#050505] text-[#F2F2F7] antialiased"
      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
    >
      <Seo
        title="Black Diamond — Facility Service"
        siteName="Black Diamond"
        description="Black Diamond is a facility-service app. Clean work, reliable service. Currently in final preparation — read our Privacy Policy or reach us at support@blackdiamondfacility.ca."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Black Diamond",
          url: "https://blackdiamondfacility.ca",
          email: SUPPORT_EMAIL,
        }}
      />
      <Grain />

      <header className="flex items-center justify-between px-6 pt-6 sm:px-10 sm:pt-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="flex items-center gap-3"
          data-testid="home-brand"
        >
          <DiamondMark size={24} />
          <span className="leading-none">
            <span className="block font-mono text-[11px] tracking-[0.32em]">
              BLACK DIAMOND
            </span>
            <span className="mt-1 block font-mono text-[8px] tracking-[0.4em] text-[#636366]">
              FACILITY SERVICE
            </span>
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="flex items-center gap-6"
        >
          <a
            data-testid="home-privacy-link"
            href="/privacy"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            Privacy Policy
          </a>
          <a
            data-testid="home-terms-link"
            href="/terms"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            Terms
          </a>
        </motion.div>
      </header>

      <main className="relative flex flex-1 items-center px-6 sm:px-10 lg:px-16">
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute right-[8%] top-1/2 hidden -translate-y-1/2 sm:block"
          style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 800 }}
          initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
          animate={{ opacity: 0.9, scale: 1, rotate: 0 }}
          transition={{ duration: 1.8, ease: EASE, delay: 0.7 }}
        >
          <DiamondMark size={220} />
        </motion.div>

        <div className="relative z-10 max-w-3xl py-24">
          <h1 className="m-0">
            <MaskedLine
              delay={0.45}
              testId="home-headline-line-1"
              className="font-serif text-[13vw] leading-[1.02] text-[#F2F2F7] sm:text-[9vw] lg:text-8xl"
            >
              Clean work,
            </MaskedLine>
            <MaskedLine
              delay={0.58}
              testId="home-headline-line-2"
              className="font-serif text-[13vw] italic leading-[1.02] text-[#A1A1A6] sm:text-[9vw] lg:text-8xl"
            >
              reliable service.
            </MaskedLine>
          </h1>
          <motion.p
            data-testid="home-subcopy"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 max-w-md text-sm leading-relaxed text-[#A1A1A6] sm:text-base"
          >
            Black Diamond is a facility-service app for booking and managing
            property care. We&apos;re putting the final polish on — in the
            meantime, our{" "}
            <a
              data-testid="home-subcopy-privacy-link"
              href="/privacy"
              className="text-[#F2F2F7] underline decoration-[#3A3A3C] underline-offset-4 transition-colors duration-300 hover:decoration-[#F2F2F7]"
            >
              Privacy Policy
            </a>{" "}
            is live, and we&apos;re one email away.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
            className="mt-10"
          >
            <a
              data-testid="home-support-email-link"
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-block border-b border-[#3A3A3C] pb-2 font-serif text-2xl text-[#F2F2F7] transition-colors duration-300 hover:border-[#F2F2F7] sm:text-3xl"
            >
              {SUPPORT_EMAIL}
            </a>
          </motion.div>
        </div>
      </main>

      <motion.footer
        data-testid="home-footer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex flex-col gap-2 border-t border-[#1C1C1E] px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-10 lg:px-16"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#636366]">
          Black Diamond — Facility Service
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#636366]">
          blackdiamondfacility.ca
        </span>
      </motion.footer>
    </div>
  );
}
